import { Fragment, type ReactNode } from 'react';
import { HEADER_FADE, REVEAL_TIMING } from '@/utils/animation';
import type { AboutBlock as AboutBlockData, RoadmapEntry } from './content';

/**
 * Our story page-specific layout helpers. All match the typography conventions
 * established on the products and articles pages — body text Karla Regular
 * 16/44 clearlab-dusk, sub-section labels SemiBold 16/18 clearlab-midnight,
 * column-title Bold 24/24 clearlab-midnight matching ArticleSection's per-row
 * title style.
 */

interface OurStoryRowProps {
  /** Title rendered in the left column (Karla Bold 24/24 -0.025em midnight). */
  title: string;
  /** Right column content (about blocks, timeline, paragraphs, etc.). */
  children: ReactNode;
  /**
   * When provided, the left-column title fades 0 → 1 over `HEADER_FADE`
   * timing. Right column always renders as-is — child components handle
   * their own reveal animations. When `undefined` the row renders statically.
   */
  visible?: boolean;
}

/**
 * Two-column row pattern shared by the three Our story content sections
 * (About us / Roadmap / Become a partner).
 *
 * Responsive layout (resize phases as the content column narrows):
 *
 *   xl+ wide (>=1040 content)  : left hits its 440 cap, right 600, gap fills
 *                                the remainder via `justify-between` (160 at
 *                                the 1200 max).
 *   middle (840…1040)          : left flexes between 240 and 440, right stays
 *                                at 600, gap = 0.
 *   floor (<840 down to mobile): left pinned at its 240 min, right shrinks
 *                                below 600 (default flex-shrink) to keep the
 *                                two columns inside the container.
 *   mobile (<md)               : 1-col stack with gap-20 (80px).
 */
export function OurStoryRow({ title, children, visible }: OurStoryRowProps) {
  const titleStyle =
    visible !== undefined
      ? {
          opacity: visible ? 1 : 0,
          transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
        }
      : undefined;

  return (
    <div className="flex w-full flex-col gap-20 md:flex-row md:items-start md:justify-between">
      <h2
        style={titleStyle}
        className="text-[24px] font-bold leading-6 tracking-[-0.025em] text-clearlab-midnight md:min-w-[240px] md:max-w-[440px] md:flex-1 xl:w-[440px] xl:flex-none"
      >
        {title}
      </h2>
      <div className="flex flex-col items-start md:w-[600px]">{children}</div>
    </div>
  );
}

interface AboutBlockProps {
  block: AboutBlockData;
}

/**
 * "Our mission" / "History" / "Vision" sub-blocks inside the About us row.
 * SemiBold 16/18 midnight label + Regular 16/44 dusk body, 8px gap.
 */
export function AboutBlock({ block }: AboutBlockProps) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
        {block.label}
      </p>
      <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
        {block.body}
      </p>
    </div>
  );
}

interface RoadmapTimelineProps {
  entries: readonly RoadmapEntry[];
  /**
   * Whether the parent section is in view. When provided, the rail and
   * each entry stagger their reveal off this single trigger:
   *   - Rail draws top → bottom via `scaleY(0 → 1)` (snappy, 500ms).
   *   - Each entry slides up + fades in over `ROW_DURATION_MS` with the
   *     shared easing, `fadeStart=0.3`, staggered 150ms apart.
   *
   * Defaults to `true` so the timeline renders fully visible when the
   * caller doesn't opt into the orchestrated animation.
   */
  visible?: boolean;
  /** ms delay applied to the rail draw (after which the rows stagger in). */
  railDelayMs?: number;
}

const RAIL_DURATION_MS = 500;
const RAIL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const ROW_STAGGER_MS = 150;
// Timeline rows use a shorter slide than the site-wide REVEAL_TIMING so the
// staggered sequence doesn't drag.
const ROW_DURATION_MS = 700;

/**
 * Roadmap timeline.
 *
 * Each Point row:
 *   - Year column (`w-10` = 40px), text-right, SemiBold 16/32, color
 *     `clearlab-steel/40`.
 *   - Then 40px gap.
 *   - Then the content cluster: 12px-wide dot column (12×12 ring, vertically
 *     centered in a 32px tall slot) + 28px gap + text column.
 *   - Text column: SemiBold 16/32 midnight title (gap-2 below) + Regular 16/44
 *     dusk body paragraphs (mb-6 between paragraphs in multi-paragraph entries).
 *
 * A single absolute vertical gradient line passes behind the dots, fading from
 * midnight at the top to transparent at the bottom — matches the home page
 * roadmap rail style.
 *
 * Inter-entry gap: `gap-20` (80px).
 */
export function RoadmapTimeline({
  entries,
  visible = true,
  railDelayMs = 0,
}: RoadmapTimelineProps) {
  // Each row's reveal kicks off 50% into the rail draw — rows stagger up
  // while the line is still finishing its second half. Uses the same easing
  // the slide uses so the row reveals match the tokenised feel.
  const firstRowDelayMs = railDelayMs + Math.round(RAIL_DURATION_MS * 0.5);
  // Opacity starts 30% into the slide, ends with it — matches the button's
  // reveal in TechnologySection.
  const fadeOffsetMs = Math.round(ROW_DURATION_MS * 0.3);
  const opacityDurationMs = ROW_DURATION_MS - fadeOffsetMs;

  return (
    <ol className="relative flex w-full flex-col gap-20">
      {/* Vertical rail behind the dots. Positioned at the center of the dot
          column (year width 40 + gap 40 + 6px to dot center = 86px). */}
      <span
        aria-hidden
        style={{
          transform: visible ? 'scaleY(1)' : 'scaleY(0)',
          transformOrigin: 'top',
          transition: `transform ${RAIL_DURATION_MS}ms ${RAIL_EASE} ${railDelayMs}ms`,
        }}
        className="pointer-events-none absolute bottom-3 left-[calc(40px+40px+5px)] top-3 w-px bg-gradient-to-b from-clearlab-midnight from-[90%] to-clearlab-oat to-[100%] md:left-[-34px]"
      />

      {entries.map((entry, idx) => {
        const rowDelayMs = firstRowDelayMs + idx * ROW_STAGGER_MS;
        return (
          <li
            key={entry.year}
            style={{
              transform: visible ? 'translateY(0)' : `translateY(${REVEAL_TIMING.offsetPx}px)`,
              opacity: visible ? 1 : 0,
              transition: `transform ${ROW_DURATION_MS}ms ${REVEAL_TIMING.ease} ${rowDelayMs}ms, opacity ${opacityDurationMs}ms ${REVEAL_TIMING.ease} ${rowDelayMs + fadeOffsetMs}ms`,
            }}
            className="relative flex w-full items-start gap-10 md:gap-0"
          >
            <p className="w-10 shrink-0 text-right text-[16px] font-semibold leading-8 tracking-[-0.025em] text-clearlab-steel/40 md:absolute md:right-[calc(100%+80px)] md:top-0">
              {entry.year}
            </p>

            <div className="flex flex-1 items-start gap-7 md:w-full md:flex-none md:gap-0">
              {/* Dot — 12×12 ring centered in a 32px tall column so it aligns
                  with the title line's vertical centre. */}
              <span
                aria-hidden
                className="mt-2.5 size-3 shrink-0 rounded-full border border-clearlab-midnight bg-[#F5F5F4] md:absolute md:right-[calc(100%+28px)] md:top-0"
              />

              <div className="flex flex-1 flex-col items-start gap-2">
                <h3 className="text-[16px] font-semibold leading-8 text-clearlab-midnight">
                  {entry.title}
                </h3>
                {entry.body.map((paragraph, pIdx) => (
                  <Fragment key={paragraph}>
                    <p
                      className={`text-[16px] font-normal leading-[44px] text-clearlab-midnight ${
                        pIdx < entry.body.length - 1 ? 'mb-6' : ''
                      }`}
                    >
                      {paragraph}
                    </p>
                  </Fragment>
                ))}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
